import React from 'react';
import { Brush, Gem, Stamp, Square, Droplets, Check, AlertTriangle, X, Footprints } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
export function FinishOptionsSection() {
  const finishes = [{
    icon: Brush,
    title: 'Broom Finish',
    description: 'The workhorse. A textured surface created by dragging a broom across fresh concrete. Reliable, affordable, and proven.',
    bestFor: ['Driveways', 'Walkways', 'Slopes', 'High-traffic areas'],
    watchOuts: ['Plain appearance', 'Some find it boring'],
    underfoot: 'Like a sidewalk—consistent grip in all conditions',
    traction: {
      label: 'Excellent',
      color: 'bg-green-100 text-green-800'
    },
    maintenance: {
      label: 'Low',
      color: 'bg-green-100 text-green-800'
    },
    cost: {
      label: '$',
      color: 'bg-green-100 text-green-800'
    }
  }, {
    icon: Gem,
    title: 'Exposed Aggregate',
    description: 'The top layer of cement is washed away to reveal decorative stone underneath. Beautiful texture with natural grip.',
    bestFor: ['Driveways', 'Patios', 'Decorative walkways'],
    watchOuts: ['Rougher surface can be uncomfortable barefoot', 'Stones can loosen over time if poorly done'],
    underfoot: 'Like a pebble beach—textured and grippy',
    traction: {
      label: 'Very Good',
      color: 'bg-green-100 text-green-800'
    },
    maintenance: {
      label: 'Medium',
      color: 'bg-yellow-100 text-yellow-800'
    },
    cost: {
      label: '$$',
      color: 'bg-yellow-100 text-yellow-800'
    }
  }, {
    icon: Stamp,
    title: 'Stamped Concrete',
    description: 'Patterns are pressed into wet concrete to mimic stone, brick, or slate. Stunning appearance but requires more care.',
    bestFor: ['Patios', 'Decorative driveways', 'Front walkways'],
    watchOuts: ['Can be slippery when wet unless texture additive is used', 'Sealer needs reapplication every 2-3 years', 'Stamped patterns can trap water'],
    underfoot: 'Like smooth stone—elegant but can be slick',
    traction: {
      label: 'Fair (needs additive)',
      color: 'bg-yellow-100 text-yellow-800'
    },
    maintenance: {
      label: 'Higher',
      color: 'bg-red-100 text-red-800'
    },
    cost: {
      label: '$$$',
      color: 'bg-red-100 text-red-800'
    }
  }, {
    icon: Square,
    title: 'Smooth / Troweled',
    description: 'A polished, smooth surface. Clean and modern but NOT recommended for outdoor heated surfaces in most cases.',
    bestFor: ['Covered patios only', 'Garage floors'],
    watchOuts: ['VERY slippery when wet', 'Not suitable for driveways or walkways', 'Even with heating, condensation creates slip risk'],
    underfoot: 'Like a polished floor—smooth and slick',
    traction: {
      label: 'Poor',
      color: 'bg-red-100 text-red-800'
    },
    maintenance: {
      label: 'Low',
      color: 'bg-green-100 text-green-800'
    },
    cost: {
      label: '$',
      color: 'bg-green-100 text-green-800'
    },
    badFit: true
  }, {
    icon: Droplets,
    title: 'Salt Finish',
    description: 'Rock salt is pressed into fresh concrete then washed out, leaving a subtle pitted texture. Unique look with decent grip.',
    bestFor: ['Patios', 'Pool decks', 'Decorative walkways'],
    watchOuts: ['Pits can collect dirt and debris', 'Not ideal for heavy vehicle traffic', 'Less common so fewer contractors experienced with it'],
    underfoot: 'Like dimpled leather—subtle texture with character',
    traction: {
      label: 'Good',
      color: 'bg-green-100 text-green-800'
    },
    maintenance: {
      label: 'Medium',
      color: 'bg-yellow-100 text-yellow-800'
    },
    cost: {
      label: '$$',
      color: 'bg-yellow-100 text-yellow-800'
    }
  }];
  return <section id="finish-options" className="py-16 bg-white md:py-24" data-id="element-206">
      <div className="container mx-auto px-4 md:px-6" data-id="element-207">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-208">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-id="element-209">
            Compare Your Finish Options
          </h2>
          <p className="mt-4 text-lg text-slate-600" data-id="element-210">
            Every finish has tradeoffs. We help you balance looks, grip, and
            maintenance.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-211">
          {finishes.map((finish, index) => <Card key={index} className={`border-2 ${finish.badFit ? 'border-red-100 bg-red-50/30' : 'border-slate-100 hover:border-primary/20'} transition-all duration-300`} data-id="element-212">
              <CardHeader className="pb-4" data-id="element-213">
                <div className="flex items-center justify-between mb-2" data-id="element-214">
                  <div className={`p-3 rounded-full ${finish.badFit ? 'bg-red-100 text-red-600' : 'bg-primary/10 text-primary'}`} data-id="element-215">
                    <finish.icon className="h-6 w-6" data-id="element-216" />
                  </div>
                  <div className="flex gap-2" data-id="element-217">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${finish.traction.color}`} data-id="element-218">
                      Traction: {finish.traction.label}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${finish.cost.color}`} data-id="element-219">
                      Cost: {finish.cost.label}
                    </span>
                  </div>
                </div>
                <CardTitle className="text-2xl" data-id="element-220">{finish.title}</CardTitle>
                <p className="text-slate-500 mt-2" data-id="element-221">{finish.description}</p>
              </CardHeader>

              <CardContent className="space-y-6" data-id="element-222">
                <div className="grid sm:grid-cols-2 gap-6" data-id="element-223">
                  <div data-id="element-224">
                    <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2 text-sm" data-id="element-225">
                      <Check className="h-4 w-4 text-green-500" data-id="element-226" /> Best For:
                    </h4>
                    <ul className="space-y-1" data-id="element-227">
                      {finish.bestFor.map((item, i) => <li key={i} className="text-sm text-slate-600 flex items-start gap-2" data-id="element-228">
                          <span className="h-1 w-1 rounded-full bg-slate-400 mt-2 shrink-0" data-id="element-229"></span>
                          {item}
                        </li>)}
                    </ul>
                  </div>

                  <div data-id="element-230">
                    <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2 text-sm" data-id="element-231">
                      <AlertTriangle className="h-4 w-4 text-amber-500" data-id="element-232" />{' '}
                      Watch-Outs:
                    </h4>
                    <ul className="space-y-1" data-id="element-233">
                      {finish.watchOuts.map((item, i) => <li key={i} className="text-sm text-slate-600 flex items-start gap-2" data-id="element-234">
                          <span className="h-1 w-1 rounded-full bg-slate-400 mt-2 shrink-0" data-id="element-235"></span>
                          {item}
                        </li>)}
                    </ul>
                  </div>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 flex items-start gap-3" data-id="element-236">
                  <Footprints className="h-5 w-5 text-slate-400 mt-0.5 shrink-0" data-id="element-237" />
                  <div data-id="element-238">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1" data-id="element-239">
                      Underfoot Feel
                    </span>
                    <p className="text-sm text-slate-700 italic" data-id="element-240">
                      "{finish.underfoot}"
                    </p>
                  </div>
                </div>

                {finish.badFit && <div className="bg-red-100 p-4 rounded-lg border border-red-200" data-id="element-241">
                    <h4 className="font-bold text-red-800 mb-1 flex items-center gap-2 text-sm" data-id="element-242">
                      <X className="h-4 w-4" data-id="element-243" /> WARNING:
                    </h4>
                    <p className="text-xs text-red-700" data-id="element-244">
                      We strongly advise against this finish for outdoor heated
                      surfaces due to slip hazards.
                    </p>
                  </div>}
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}